import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  FolderIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

interface PropertyGroup {
  id: string;
  name: string;
  slug: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    properties: number;
  };
  properties?: Array<{
    id: string;
    name: string;
    slug: string;
    address: string;
    city: string;
    state: string;
  }>;
}

interface PropertyGroupFormData {
  name: string;
  slug: string;
  description: string;
}

const PropertyGroups: React.FC = () => {
  const { user } = useAuth();
  const [propertyGroups, setPropertyGroups] = useState<PropertyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PropertyGroup | null>(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [formData, setFormData] = useState<PropertyGroupFormData>({
    name: '',
    slug: '',
    description: ''
  });

  // Auto-clear messages after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchPropertyGroups = useCallback(async () => {
    if (!user?.token) return;

    try {
      const response = await axios.get<PropertyGroup[]>('/api/property-groups', {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      setPropertyGroups(response.data);
    } catch (error) {
      console.error('Error fetching property groups:', error);
      setMessage('Failed to load property groups');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    if (user?.role === 'ADMIN') {
      fetchPropertyGroups();
    } else {
      setLoading(false);
    }
  }, [fetchPropertyGroups, user?.role]);

  const generateSlug = (name: string) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: generateSlug(name)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.token) return;

    try {
      if (editingGroup) {
        // Update existing property group
        await axios.put(`/api/property-groups/${editingGroup.id}`, formData, {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        });
        setMessage('Property group updated successfully');
      } else {
        // Create new property group
        await axios.post('/api/property-groups', formData, {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        });
        setMessage('Property group created successfully');
      }
      
      setMessageType('success');
      setShowAddModal(false);
      setEditingGroup(null);
      setFormData({
        name: '',
        slug: '',
        description: ''
      });
      await fetchPropertyGroups();
    } catch (error: any) {
      console.error('Error saving property group:', error);
      setMessage(error.response?.data?.message || 'Failed to save property group');
      setMessageType('error');
    }
  };

  const handleEdit = (group: PropertyGroup) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      slug: group.slug,
      description: group.description || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (groupId: string, groupName: string) => {
    if (!user?.token) return;
    
    if (!window.confirm(`Are you sure you want to delete "${groupName}"? This will unassign all properties from this group.`)) {
      return;
    }

    try {
      await axios.delete(`/api/property-groups/${groupId}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      setMessage('Property group deleted successfully');
      setMessageType('success');
      await fetchPropertyGroups();
    } catch (error: any) {
      console.error('Error deleting property group:', error);
      setMessage(error.response?.data?.message || 'Failed to delete property group');
      setMessageType('error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: ''
    });
    setEditingGroup(null);
    setShowAddModal(false);
  };

  if (user?.role !== 'ADMIN') {
    return (
      <div className="text-center py-8">
        <p className="text-white/60">You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#22d3ee] mx-auto"></div>
        <p className="text-white/60 mt-2">Loading property groups...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Property Categories</h1>
          <p className="text-white/60 mt-1">Organize your properties into logical groups</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <PlusIcon className="w-5 h-5" />
          <span>Add Category</span>
        </button>
      </div>

      {/* Success/Error Messages */}
      {message && (
        <div className={`p-4 rounded-xl border ${
          messageType === 'success' 
            ? 'bg-green-500/10 border-green-500/20 text-green-400' 
            : 'bg-red-500/10 border-red-500/20 text-red-400'
        }`}>
          {message}
        </div>
      )}

      {/* Property Groups Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {propertyGroups.map((group) => (
          <div key={group.id} className="floating-card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center">
                  <FolderIcon className="w-6 h-6 text-[#22d3ee]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{group.name}</h3>
                  <p className="text-sm text-white/60">/{group.slug}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleEdit(group)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <PencilIcon className="w-4 h-4 text-white/60 hover:text-white" />
                </button>
                <button
                  onClick={() => handleDelete(group.id, group.name)}
                  className="p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                >
                  <TrashIcon className="w-4 h-4 text-white/60 hover:text-red-400" />
                </button>
              </div>
            </div>

            <div className="space-y-2 text-sm text-white/60">
              <p>{group.description || 'No description provided'}</p>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex justify-between items-center text-sm">
                <span className="text-white/60">Properties:</span>
                <span className="text-white font-medium">{group._count?.properties || 0}</span>
              </div>
              {group.properties && group.properties.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-white/40 mb-1">Properties in this category:</p>
                  <div className="flex flex-wrap gap-1">
                    {group.properties.slice(0, 3).map((property) => (
                      <span key={property.id} className="text-xs px-2 py-1 bg-white/10 rounded text-white/60">
                        {property.name}
                      </span>
                    ))}
                    {group.properties.length > 3 && (
                      <span className="text-xs px-2 py-1 bg-white/10 rounded text-white/60">
                        +{group.properties.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {propertyGroups.length === 0 && (
          <div className="col-span-full text-center py-12">
            <FolderIcon className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white/60 mb-2">No Property Categories Yet</h3>
            <p className="text-white/40 mb-4">Get started by creating your first property category</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary"
            >
              Create Your First Category
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Property Group Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1a1b] border border-white/10 rounded-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">
                {editingGroup ? 'Edit Category' : 'Add New Category'}
              </h3>
              <button
                onClick={resetForm}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-white/60" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="input-field"
                  placeholder="Apartment Communities"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  URL Slug *
                </label>
                <input
                  type="text"
                  required
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  className="input-field"
                  placeholder="apartment-communities"
                />
                <p className="text-xs text-white/40 mt-1">
                  Used in URLs. Auto-generated from name.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="input-field resize-none"
                  rows={3}
                  placeholder="Brief description of this property category..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 flex items-center justify-center space-x-2"
                >
                  <CheckIcon className="w-4 h-4" />
                  <span>{editingGroup ? 'Update' : 'Create'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyGroups;
